import { useState } from "react";
import { Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { internalTransfersApi, type InternalTransferListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];
const STATUS_LABEL: Record<string, string> = {
  Draft: "Draft", Submitted: "Submitted", Approved: "Approved",
  Posted: "Dispatched", Cancelled: "Cancelled",
};
const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/**
 * UC-M1-INV-07 — Internal transfers between same-branch warehouses.
 * Dispatched documents show non-zero "In transit" until the destination receives.
 */
export function InternalTransfersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("inventory.transfer.internal"));

  const query = useQuery({
    queryKey: ["internal-transfers", status, pagination.page, pagination.pageSize],
    queryFn: () => internalTransfersApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<InternalTransferListDto>[] = [
    { field: "documentNo", headerName: "Transfer #", width: 160 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "" },
    { field: "sourceWarehouseName", headerName: "From", flex: 1, minWidth: 160 },
    { field: "destWarehouseName", headerName: "To", flex: 1, minWidth: 160 },
    { field: "status", headerName: "Status", width: 130,
      renderCell: (p) => (
        <Chip size="small"
          label={STATUS_LABEL[p.value as string] ?? p.value}
          color={STATUS_COLOR[p.value as string] ?? "default"} />
      )
    },
    { field: "lineCount", headerName: "Lines", width: 80, type: "number" },
    { field: "totalQty", headerName: "Total qty", width: 110, type: "number" },
    { field: "inTransitQty", headerName: "In transit", width: 110, type: "number",
      renderCell: (p) => Number(p.value) > 0 ? (
        <Chip size="small" color="warning" label={String(p.value)} />
      ) : <>{p.value as number}</> },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Internal transfers</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate("/inventory/internal-transfers/new")}>
            New transfer
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/inventory/internal-transfers/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
